import { inject, injectable } from 'inversify-props';
import { IHttpClient, ILogger, ISettings } from '../common';
import { PagedResult, DeckVariation } from '../../models';


export interface IDeckVariationService  {
	get(deckId: string, id: string): Promise<DeckVariation>;
	page(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckVariation>>;
	list(deckId: string): Promise<DeckVariation[]>;
	save(model: DeckVariation): Promise<DeckVariation>;
	delete(deckId: string, variationId: string): Promise<any>;
	autocomplete(criteria: string): Promise<string[]>;
}


@injectable()
export class DeckVariationService implements IDeckVariationService {

	constructor(
		@inject('IHttpClient') private httpClient: IHttpClient,
		@inject('ILogger') private logger: ILogger,
		@inject('ISettings') private settings: ISettings) { }

	public get(deckId: string, id: string): Promise<DeckVariation> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/variations/${id}`;
		return this.httpClient.get<DeckVariation>(url);
	}

	public page(deckId: string, pageIndex: number, pageSize: number): Promise<PagedResult<DeckVariation>> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/variations/${pageIndex}/${pageSize}`;
		return this.httpClient.get<PagedResult<DeckVariation>>(url);
	}

	public list(deckId: string): Promise<DeckVariation[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/variations/`;
		return this.httpClient.get<DeckVariation[]>(url);
	}

	public autocomplete(criteria: string): Promise<string[]> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/_/variations/autocomplete/${criteria}`;
		return this.httpClient.get<string[]>(url);
	}

	public save(model: DeckVariation): Promise<DeckVariation> {
		const payload: { [k: string]: any } = {
			id: model.id,
			deckId: model.deckId,
			notes: model.notes,
			label: model.label,
			truckMountId: model.truckMountId,
			surfaceId: model.surfaceId,
			shapeId: model.shapeId,
			sizeId: model.sizeId,
			year: model.year ? parseInt(model.year.toString(), null) : null,
			width: model.width ? parseFloat(model.width.toString()) : null,
			length: model.length ? parseFloat(model.length.toString()) : null,
			tailLength: model.tailLength ? parseFloat(model.tailLength.toString()) : null,
			noseLength: model.noseLength ? parseFloat(model.noseLength.toString()) : null,
			numberOfPlys: model.numberOfPlys ? parseInt(model.numberOfPlys.toString(), null) : null,
			wheelBase: model.wheelBase ? parseFloat(model.wheelBase.toString()) : null,
			isReissue: model.isReissue,
			isEnabled: model.isEnabled,
			isApproxYear: model.isApproxYear,
		};
		if (model.technologies && model.technologies.length) { payload.technologyIds = model.technologies.map((x) => x.id); }

		let url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${model.deckId}/variations`;
		if (model.id !== null && model.id !== undefined && model.id) {
			url += `/${model.id}`;
			return this.httpClient.put<DeckVariation>(url, payload);
		}
		return this.httpClient.post<DeckVariation>(url, payload);
	}

	public delete(deckId: string, variationId: string): Promise<any> {
		const url = `${this.settings.baseUrl}${this.settings.deckBaseUrl}/${deckId}/variations/${variationId}`;
		return this.httpClient.delete(url);
	}

}
